/**
 * nativeCallBridge.js — Native (CallKit) ↔ JS köprüsü
 * ---------------------------------------------------------------------------
 * iOS tarafındaki `AileCallManager.swift`, olayları şu şekilde yollar:
 *     window.dispatchEvent(new CustomEvent('fp:nativeCall', { detail: {...} }))
 *
 * Bu modül o olayları dinler, VoIP token'ını sunucuya kaydeder ve arama
 * eylemlerini (cevapla / reddet / sustur) uygulama içi arama arayüzüne iletir.
 *
 * AKIŞ — uygulama KAPALIYKEN gelen arama:
 *   1. Sunucu VoIP push yollar
 *   2. iOS uygulamayı uyandırır, CallKit tam ekran arama gösterir (zil çalar)
 *   3. Kullanıcı "Cevapla"ya basar
 *   4. Swift → 'answer' olayı → merkezi callManager aramayı eşler
 *
 * NOT: Bu dosyada POLLING YOKTUR. Tüm olaylar native taraftan itilir.
 * ---------------------------------------------------------------------------
 */

import { Capacitor } from "@capacitor/core";
import { voipTokenKaydet, voipTokenSil } from "./callService";
import { ensureAccessToken } from "./apiClient";

const OLAY_ADI = "fp:nativeCall";

/* BEKLEYEN CEVAP KUYRUĞU
   ─────────────────────────────────────────────────────────────────────────
   Uygulama KAPALIYKEN CallKit'ten "Cevapla"ya basıldığında olay, React
   arama yöneticisi hazır olmadan gelebilir. Olay o anda kaybolursa kullanıcı
   ana sayfaya bakar ve görüşme kurulmaz.

   Bu yüzden cevap burada TUTULUR; App.js ilgili odayı açar ve merkezi
   callManager hazır olduğunda `bekleyenCevabiTuket()` ile teslim alır. */
let bekleyenCevap = null;
const bekleyenNativeOlaylar = [];
const BEKLEYEN_OLAY_LIMITI = 20;

/* CallKit'in GERÇEKTEN gösterdiği aramalar.
   iOS'ta gelen arama arayüzü normalde CallKit'ten gelir. Ancak VoIP push
   herhangi bir sebeple ulaşmazsa (token tazelenmemiş, ağ, APNs gecikmesi)
   hiçbir şey görünmüyordu — tek noktadan arıza. Artık CallKit'in hangi
   aramaları gösterdiğini kaydediyoruz; SSE'den gelen arama BURADA YOKSA
   uygulama içi arayüz YEDEK olarak devreye giriyor. */
const callKitGosterilen = new Set();

/** CallKit bu aramayı gösterdi mi? */
export function callKitGosterdiMi(callId) {
  return !!callId && callKitGosterilen.has(String(callId));
}

/** Bekleyen cevabı okur ve kuyruktan siler (tek kullanımlık). */
export function bekleyenCevabiTuket(roomId = null) {
  if (!bekleyenCevap) return null;
  if (roomId && bekleyenCevap.roomId && String(bekleyenCevap.roomId) !== String(roomId)) {
    return null; // Başka odanın cevabı — dokunma
  }
  const c = bekleyenCevap;
  bekleyenCevap = null;
  return c;
}

/** Cevap 60 saniyeden eskiyse geçersiz sayılır. */
function bekleyenCevabiYaz(callId, roomId, familyId = null) {
  bekleyenCevap = { callId, roomId, familyId, familyFirebaseId: familyId, ts: Date.now() };
  setTimeout(() => {
    if (bekleyenCevap && Date.now() - bekleyenCevap.ts >= 60000) bekleyenCevap = null;
  }, 61000);
}

// Merkezi arama yöneticisi ve uygulama arayüzü aboneleri
const aboneler = new Set();

let kuruldu = false;
let sonToken = null;
let aktifFamilyId = null;
let iosAudioAktif = false;
let iosKopruHazir = false;
let iosHazirTimer = null;
const iosAudioBekleyenler = new Set();
const islenenNativeOlaylar = new Set();
const answerEventIds = new Map();
let nativeSesSirasi = Promise.resolve();
let sonNativeOturum = null;
let voipKayitState = null;
let sonBasariliVoipKayit = "";

/** Android AudioManager ve iOS ses oturumu işlemlerini sıraya koyar.
    Önceki aramanın reset'i, hızlıca başlayan yeni aramanın prepare işleminin
    arkasından çalışıp sesi kesemez. */
function nativeSesIsleminiSirala(islem) {
  const sonuc = nativeSesSirasi
    .catch(() => {})
    .then(() => (typeof islem === "function" ? islem() : true));
  nativeSesSirasi = sonuc.catch(() => {});
  return sonuc;
}

const yerelGecikme = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function iosAudioDurumuAta(aktif) {
  iosAudioAktif = !!aktif;
  if (!iosAudioAktif) return;
  iosAudioBekleyenler.forEach((tamamla) => {
    try { tamamla(true); } catch (_) { /* yoksay */ }
  });
  iosAudioBekleyenler.clear();
}

/** Kararlı bir cihaz kimliği — VoIP token'ı bu kimliğe bağlanır. */
function cihazKimligi() {
  try {
    let id = localStorage.getItem("fp:deviceId");
    if (!id) {
      id = `ios-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("fp:deviceId", id);
    }
    return id;
  } catch (_) {
    return null;
  }
}

/**
 * Native arama olaylarına abone olur.
 * Geriye aboneliği iptal eden fonksiyon döner.
 *
 * Olay tipleri: answer | end | mute | incoming | audioActive | reset
 */
export function nativeAramaOlaylarinaAbone(handler) {
  if (typeof handler !== "function") return () => {};
  aboneler.add(handler);
  if (bekleyenNativeOlaylar.length) {
    const kuyruk = bekleyenNativeOlaylar.splice(0);
    Promise.resolve().then(() => {
      kuyruk.forEach((olay) => {
        try { handler(olay); } catch (_) { /* diğer olaylara devam */ }
      });
    });
  }
  return () => aboneler.delete(handler);
}

function dagit(detay) {
  if (aboneler.size === 0) {
    bekleyenNativeOlaylar.push(detay);
    if (bekleyenNativeOlaylar.length > BEKLEYEN_OLAY_LIMITI) bekleyenNativeOlaylar.shift();
    return;
  }
  aboneler.forEach((fn) => {
    try {
      fn(detay);
    } catch (_) {
      // Bir abonenin hatası diğerlerini etkilemesin
    }
  });
}

async function tokenKaydet(token) {
  const deviceId = cihazKimligi();
  if (!deviceId || !token) return;
  sonToken = token;

  // PushKit tokenı React oturumu ve aktif aile hazır olmadan gelebilir.
  // En güncel token bellekte tutulur; auth/family hazır olduğunda aşağıdaki
  // event ve familyIdAta akışları kaydı tek kez tamamlar.
  if (!aktifFamilyId) return;

  try {
    const accessToken = await ensureAccessToken({ source: "voip_token" });
    if (!accessToken || sonToken !== token || !aktifFamilyId) return;

    const familyId = String(aktifFamilyId);
    const kayitAnahtari = `${deviceId}:${token}:${familyId}`;
    if (sonBasariliVoipKayit === kayitAnahtari) return;
    if (voipKayitState?.key === kayitAnahtari) return voipKayitState.promise;

    const state = { key: kayitAnahtari, promise: null };
    state.promise = voipTokenKaydet({ deviceId, voipToken: token, familyId })
      .then((result) => {
        if (sonToken === token && String(aktifFamilyId || "") === familyId) {
          sonBasariliVoipKayit = kayitAnahtari;
        }
        return result;
      })
      .finally(() => {
        if (voipKayitState === state) voipKayitState = null;
      });
    voipKayitState = state;
    await state.promise;
  } catch (_) {
    // Ağ hatası — bir sonraki açılışta tekrar denenecek
  }
}

async function tokenKaydiniSil(deviceId) {
  if (!deviceId) return;
  try {
    // Devam eden kayıt isteğinin silme isteğinden sonra tamamlanıp kaydı geri
    // getirmemesi için önce onu bitir, ardından yetkili silmeyi gönder.
    await voipKayitState?.promise?.catch(() => {});
    const accessToken = await ensureAccessToken({ source: "voip_token_delete" });
    if (!accessToken) return;
    await voipTokenSil(deviceId);
    sonBasariliVoipKayit = "";
  } catch (_) {
    // Oturum yoksa veya ağ geçiciyse gereksiz yetkisiz istek gönderme.
  }
}

/**
 * Köprüyü kurar. Uygulama açılışında bir kez çağrılır (App.js).
 * iOS dışında hiçbir şey yapmaz.
 */
export function nativeCallBridgeKur() {
  if (kuruldu) return;
  if (typeof window === "undefined") return;
  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") return;

  kuruldu = true;

  const nativeOlayiIsle = (d) => {
    if (!d || typeof d.type !== "string") return;
    const eventId = d.eventId ? String(d.eventId) : "";
    if (eventId && islenenNativeOlaylar.has(eventId)) return;
    if (eventId) {
      islenenNativeOlaylar.add(eventId);
      if (islenenNativeOlaylar.size > 100) {
        islenenNativeOlaylar.delete(islenenNativeOlaylar.values().next().value);
      }
    }

    if (d.type === "voipToken" && d.token) {
      // Yalnızca iOS: PushKit ayrı bir token üretir. Android'de arama push'u
      // mevcut FCM token'ı üzerinden gider, ek kayıt gerekmez.
      tokenKaydet(d.token);
      return;
    }
    if (d.type === "voipTokenInvalid") {
      sonToken = null;
      sonBasariliVoipKayit = "";
      const deviceId = cihazKimligi();
      if (deviceId) tokenKaydiniSil(deviceId);
      return;
    }
    if (d.type === "nativeAccessToken" && d.accessToken) {
      // Native motor, uygulama arka plandayken refresh yaptıysa WebView
      // açıldığında aynı yeni token'ı kullanmalı.
      try {
        localStorage.setItem("fp:accessToken", String(d.accessToken));
        window.dispatchEvent(new CustomEvent("fp:accessTokenChanged", {
          detail: { accessToken: String(d.accessToken) },
        }));
      } catch (_) { /* Keychain'deki oturum çalışmaya devam eder */ }
      return;
    }
    if (d.type === "bridgeReady") {
      iosKopruHazir = true;
      if (iosHazirTimer) {
        clearTimeout(iosHazirTimer);
        iosHazirTimer = null;
      }
      try {
        window.webkit?.messageHandlers?.aileCallPending?.postMessage({});
      } catch (_) { /* bekleyen cevap yoksa işlem gerekmez */ }
      if (sonNativeOturum) {
        try {
          window.webkit?.messageHandlers?.aileCallSession?.postMessage(sonNativeOturum);
        } catch (_) { /* bir sonraki oturum/token değişiminde yeniden gider */ }
      }
      return;
    }

    if (d.type === "audioActive") iosAudioDurumuAta(true);
    if (d.type === "audioInactive" || d.type === "reset") iosAudioDurumuAta(false);

    // CallKit bir aramayı gösterdiğinde işaretle (yedek yolun kapanması için).
    if ((d.type === "incoming" || d.type === "answer") && d.callId) {
      callKitGosterilen.add(String(d.callId));
      // Sonsuza kadar büyümesin
      if (callKitGosterilen.size > 50) {
        callKitGosterilen.delete(callKitGosterilen.values().next().value);
      }
    }
    if ((d.type === "end" || d.type === "cancel") && d.callId) {
      callKitGosterilen.delete(String(d.callId));
    }

    // Cevaplama olayını, odayı açan tarafın tüketebilmesi için sakla.
    if (d.type === "answer" && d.callId) {
      if (eventId) answerEventIds.set(String(d.callId), String(eventId));
      const eventFamilyId = d.familyId || d.familyFirebaseId || null;
      bekleyenCevabiYaz(
        String(d.callId),
        d.roomId ? String(d.roomId) : null,
        eventFamilyId ? String(eventFamilyId) : null
      );
    }

    dagit(d);

    if (platform === "android" && eventId && d.type !== "answer") {
      const p = androidEklenti();
      if (p && typeof p.bekleyenOlayiTuket === "function") {
        p.bekleyenOlayiTuket({ eventId }).catch(() => {});
      }
    }
  };

  window.addEventListener(OLAY_ADI, (e) => nativeOlayiIsle(e?.detail));
  window.addEventListener("fp:accessTokenChanged", (e) => {
    if (e?.detail?.accessToken && sonToken && aktifFamilyId) tokenKaydet(sonToken);
  });

  /* Swift tarafına "JS hazır" haberi.
     Uygulama kapalıyken gelen aramada Swift, JS yüklenmeden önce olay
     üretebilir; o olaylar native tarafta kuyrukta bekler ve bu çağrıdan
     sonra boşaltılır. Böylece hiçbir arama kaybolmaz. */
  if (platform === "ios") {
    /* Cold start sırasında React, WKScriptMessageHandler eklenmeden önce
       yüklenebilir. Tek seferlik hazır mesajı o anda kaybolursa Swift'in
       kuyruğundaki CallKit `answer` olayı hiç JS'e ulaşmaz. Swift
       `bridgeReady` ile onaylayana kadar yalnızca yerel köprüye kısa aralıklarla
       tekrar bildir; sunucu isteği veya polling yoktur. */
    let deneme = 0;
    const hazirBildir = () => {
      if (iosKopruHazir || deneme >= 80) {
        iosHazirTimer = null;
        return;
      }
      deneme += 1;
      try {
        window.webkit?.messageHandlers?.aileCallReady?.postMessage({});
      } catch (_) { /* native köprü bir sonraki denemede hazır olabilir */ }
      iosHazirTimer = setTimeout(hazirBildir, 400);
    };
    hazirBildir();
  } else if (platform === "android") {
    const p = androidEklenti();
    if (p && typeof p.bekleyenOlayiAl === "function") {
      p.bekleyenOlayiAl().then((d) => {
        if (d && typeof d.type === "string" && d.type) nativeOlayiIsle(d);
      }).catch(() => {});
    }
  }
}

/**
 * Android: bildirimden "Reddet" basıldığında uygulama açılmadan sunucuya
 * bilgi gönderilebilmesi için oturum bilgisini native tarafa yazar.
 * Veriler uygulamaya ÖZEL depoya gider (WebView localStorage ile aynı koruma).
 */
export function oturumBilgisiniNativeYaz(
  token,
  apiBase,
  { refreshToken = "", familyId = "", uid = "" } = {}
) {
  // iOS: erişim/refresh token yalnız Keychain'e yazılır. UserDefaults veya
  // push payload içinde tutulmaz. Yerel motor kilit ekranından cevap verirken
  // bu oturumla mevcut REST/SSE uç noktalarına bağlanır.
  try {
    sonNativeOturum = {
      accessToken: token || "",
      refreshToken: refreshToken || "",
      apiBase: apiBase || "",
      familyId: familyId || "",
      uid: uid || "",
    };
    if (Capacitor.getPlatform() === "ios"
        && window.webkit?.messageHandlers?.aileCallSession) {
      window.webkit.messageHandlers.aileCallSession.postMessage(sonNativeOturum);
    }
  } catch (_) { /* iOS dışında veya köprü hazır değil */ }

  const p = androidEklenti();
  if (p && typeof p.oturumBilgisiKaydet === "function") {
    p.oturumBilgisiKaydet({ token: token || "", apiBase: apiBase || "" }).catch(() => {});
  }
}

/** Aile değiştiğinde token'ı yeni aileye bağla. */
export function familyIdAta(familyId) {
  aktifFamilyId = familyId || null;
  if (sonToken) tokenKaydet(sonToken);
}

/** Çıkış yapılınca token'ı sil — başkasının cihazına arama düşmesin. */
export async function voipKaydiniKaldir() {
  const deviceId = cihazKimligi();
  sonToken = null;
  sonBasariliVoipKayit = "";
  if (!deviceId) return;
  await tokenKaydiniSil(deviceId);
}

/** iOS'ta CallKit gelen arama arayüzünü yönetiyor mu? */
export function callKitAktifMi() {
  try {
    return Capacitor.getPlatform() === "ios";
  } catch (_) {
    return false;
  }
}

/**
 * Ses çıkışını hoparlör ↔ ahize arasında değiştirir.
 * @param {boolean} hoparlor true → hoparlör, false → ahize
 */
export function nativeHoparlor(hoparlor) {
  // iOS
  try {
    if (window.webkit?.messageHandlers?.aileCallSpeaker) {
      return nativeSesIsleminiSirala(() => {
        window.webkit.messageHandlers.aileCallSpeaker.postMessage({ speaker: !!hoparlor });
        return true;
      });
    }
  } catch (_) { /* yoksay */ }

  // Android — AudioManager üzerinden gerçek yönlendirme
  const p = androidEklenti();
  if (p && typeof p.hoparlorAyarla === "function") {
    return nativeSesIsleminiSirala(() => p.hoparlorAyarla({ speaker: !!hoparlor }));
  }
  return false;
}

/** CallKit'in AVAudioSession'ı etkinleştirmesini olay tabanlı bekler. */
export function nativeSesHazirBekle(timeoutMs = 10000) {
  try {
    if (Capacitor.getPlatform() !== "ios" || iosAudioAktif) return Promise.resolve(true);
  } catch (_) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let bitti = false;
    const tamamla = (sonuc) => {
      if (bitti) return;
      bitti = true;
      clearTimeout(timer);
      iosAudioBekleyenler.delete(tamamla);
      resolve(sonuc);
    };
    const timer = setTimeout(() => tamamla(false), Math.max(1000, Number(timeoutMs) || 10000));
    iosAudioBekleyenler.add(tamamla);
  });
}

/** Yeni çağrı için platform ses yolunu ahize varsayımıyla hazırlar. */
export async function nativeSesliAramayaHazirla() {
  return nativeSesIsleminiSirala(async () => {
    const platform = Capacitor.getPlatform();
    if (platform === "ios") {
      const hazir = await nativeSesHazirBekle(12000);
      if (!hazir) {
        const error = new Error("IOS_AUDIO_SESSION_NOT_ACTIVE");
        error.code = "IOS_AUDIO_SESSION_NOT_ACTIVE";
        throw error;
      }
      return true;
    }
    if (platform === "android") {
      const p = androidEklenti();
      if (p && typeof p.sesliAramayaHazirla === "function") {
        await p.sesliAramayaHazirla();
      }
    }
    return true;
  });
}

/** iOS giden aramasını CallKit'e kaydeder; Telefon geçmişi de buradan oluşur. */
export async function nativeGidenAramaBaslat({ callId, roomId, displayName = "Aile Yönetimi" }) {
  if (Capacitor.getPlatform() !== "ios" || !window.webkit?.messageHandlers?.aileCallStart) {
    return true;
  }
  return nativeSesIsleminiSirala(async () => {
    iosAudioDurumuAta(false);
    window.webkit.messageHandlers.aileCallStart.postMessage({
      callId: String(callId || ""),
      roomId: String(roomId || ""),
      displayName: String(displayName || "Aile Yönetimi"),
    });
    // Medya motoru Swift tarafında başlar. CallKit'in didActivate olayı sesi
    // açar; JS'in veya sohbet ekranının yüklenmesini beklemeyiz.
    return true;
  });
}

/**
 * Uygulama içindeki gelen-arama düğmesini gerçek CallKit cevap akışına sokar.
 * Push gecikmişse Swift aynı callId ile yerel CallKit kaydı oluşturup cevaplar.
 */
export function nativeGelenAramayiCevapla({
  callId,
  roomId = "",
  familyId = "",
  fromUid = "",
  fromName = "Aile Yönetimi",
  fromAvatar = "",
  isGroup = false,
} = {}) {
  try {
    if (Capacitor.getPlatform() !== "ios" || !callId) return false;
    const handler = window.webkit?.messageHandlers?.aileCallAnswer;
    if (!handler) return false;
    iosAudioDurumuAta(false);
    handler.postMessage({
      callId: String(callId),
      roomId: String(roomId || ""),
      familyId: String(familyId || ""),
      familyFirebaseId: String(familyId || ""),
      fromUid: String(fromUid || ""),
      fromName: String(fromName || "Aile Yönetimi"),
      fromAvatar: String(fromAvatar || ""),
      isGroup: !!isGroup,
    });
    return true;
  } catch (_) {
    return false;
  }
}

/** Mevcut/gelen aramayı yerel iOS motorunda başlatır. */
export function nativeIOSAramayaKatil(callId, roomId, familyId = "") {
  try {
    if (Capacitor.getPlatform() !== "ios"
        || !window.webkit?.messageHandlers?.aileCallJoin) return false;
    window.webkit.messageHandlers.aileCallJoin.postMessage({
      callId: String(callId || ""),
      roomId: String(roomId || ""),
      familyId: String(familyId || ""),
      familyFirebaseId: String(familyId || ""),
    });
    return true;
  } catch (_) {
    return false;
  }
}

export function nativeIOSSustur(muted) {
  try {
    if (Capacitor.getPlatform() !== "ios"
        || !window.webkit?.messageHandlers?.aileCallMute) return false;
    window.webkit.messageHandlers.aileCallMute.postMessage({ muted: !!muted });
    return true;
  } catch (_) {
    return false;
  }
}

export function nativeIOSMotoruMu() {
  try { return Capacitor.getPlatform() === "ios"; }
  catch (_) { return false; }
}

/** Gerçek WebRTC bağlantısı kurulduğunda CallKit süresini başlatır. */
export function nativeAramaBaglandi() {
  try {
    if (window.webkit?.messageHandlers?.aileCallConnected) {
      window.webkit.messageHandlers.aileCallConnected.postMessage({});
    }
  } catch (_) { /* yoksay */ }
}

/** CallKit cevabı backend ready aşamasına ulaştıktan sonra kalıcı native
    kuyruğu temizler. Başarısız katılımda cevap saklı kalır ve yeniden açılışta
    güvenli biçimde tekrar teslim edilir. */
export function nativeCevapIslendi(callId) {
  if (!callId) return;
  try {
    window.webkit?.messageHandlers?.aileCallAnswerConsumed?.postMessage({
      callId: String(callId),
    });
  } catch (_) { /* iOS dışında işlem yok */ }
  const eventId = answerEventIds.get(String(callId));
  if (eventId) {
    answerEventIds.delete(String(callId));
    const p = androidEklenti();
    if (p && typeof p.bekleyenOlayiTuket === "function") {
      p.bekleyenOlayiTuket({ eventId }).catch(() => {});
    }
  }
}

/** Eski çağıranlarla uyumluluk; AVAudioSession artık manuel sıfırlanmaz. */
export function nativeSesTazele() { return false; }

/** Android tarafındaki AileCall eklentisi (varsa). */
function androidEklenti() {
  try {
    if (Capacitor.getPlatform() !== "android") return null;
    return Capacitor.Plugins?.AileCall || null;
  } catch (_) {
    return null;
  }
}

/**
 * Android'de giden arama bekleme tonunu zil/sessiz durumundan bağımsız olan
 * telefon-görüşmesi ses kanalında çalar. Eklenti eskiyse false döner ve JS
 * tarafı mevcut Web Audio tonunu yedek olarak kullanır.
 */
export function nativeBeklemeTonuBaslat() {
  const p = androidEklenti();
  if (!p || typeof p.beklemeTonuBaslat !== "function") return false;
  p.beklemeTonuBaslat().catch(() => {});
  return true;
}

export function nativeBeklemeTonuDurdur() {
  const p = androidEklenti();
  if (!p || typeof p.beklemeTonuDurdur !== "function") return false;
  p.beklemeTonuDurdur().catch(() => {});
  return true;
}

/**
 * Konuşma başladığında çağrılır.
 * Android'de mikrofon ön plan servisini başlatır — Android 14+ bunu zorunlu
 * tutar, yoksa uygulama arka plana alındığında sistem mikrofonu keser.
 * iOS'ta gerek yoktur (CallKit ses oturumunu kendisi yönetir).
 */
export function nativeAramaBasladi() {
  const p = androidEklenti();
  if (p && typeof p.aramaBasladi === "function") {
    p.aramaBasladi().catch(() => {});
  }
}

/** Uygulama içinden arama bitince native arayüzü de kapat. */
export function nativeAramayiKapat(callId = "") {
  iosAudioDurumuAta(false);
  return nativeSesIsleminiSirala(async () => {
    try {
      if (window.webkit?.messageHandlers?.aileCallEnd) {
        window.webkit.messageHandlers.aileCallEnd.postMessage({
          callId: String(callId || ""),
        });
        /* CallKit'in eski AVAudioSession'ı bırakmasına kısa süre tanı. Yeni
           aramanın prepare işlemi bu kuyruğun arkasında bekler. */
        await yerelGecikme(350);
      }
    } catch (_) { /* iOS dışında işlem yok */ }

    const p = androidEklenti();
    if (p) {
      if (typeof p.aramaBitti === "function") await p.aramaBitti().catch(() => {});
      if (typeof p.sesModunuSifirla === "function") {
        await p.sesModunuSifirla().catch(() => {});
      }
    }
    return true;
  });
}
